import { Icon, Text, Tabs, Tab, Card, Row, Column, Title, ButtonSelect } from '@upsales/components';
import IndustryInsightsCard from './IndustryInsightsContent/IndustryInsightsCard';
import { NEXT_STEPS } from '../Billing/BillingAddonCard';
import BillingAddonBanner from 'App/components/BillingAddonBanner';
import ClientIndustryResource from 'Resources/ClientIndustry';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { makeCancelable } from 'App/babel/helpers/promise';
import bemClass from '@upsales/components/Utils/bemClass';
import logError from 'App/babel/helpers/logError';
import FinancialChart from './FinancialChart';
import T from 'Components/Helpers/translate';
import { translateSniCode } from 'Helpers/sniCodeHelper';
import FinancialCard from './FinancialCard';
import PropTypes from 'prop-types';
import './FinancialTab.scss';
import React from 'react';

class FinancialTab extends React.Component {
	alias = 'PROSPECTING_PRO_SIGNALS';
	constructor(props) {
		super(props);
		const t = Tools.$translate;

		this.ECONOMY_CHART_ORDER_PARAM_ID = 234;

		this.self = Tools.AppService.getSelf() || {};
		this.chartOrder = this.self?.userParams?.economyChartOrder || 'desc';
		this.showPlaceholder = !Tools.FeatureHelper.isAvailable(Tools.FeatureHelper.Feature.PROSPECTING_PRO);
		this.hasReactClientCard = Tools.FeatureHelper.hasSoftDeployAccess('REACT_CLIENT_CARD');
		this.hasIndustryInsights = Tools.FeatureHelper.isAvailable(Feature.AI);
		this.state = {
			tab: this.hasIndustryInsights ? 'companyKeyFigures' : 'incomeStatement',
			sticky: false,
			width: null,
			availableAddons: [],
			failedToFetchAddons: false,
			industryData: null,
			industryLoading: false,
			hasCompanyComparisonRef: false,
			chartOrder: this.chartOrder
		};
		this.currency = this.props.companyData?.[0]?.nAccountCurrencyInfo || 'SEK';
		this.tabRefs = {
			...(this.hasIndustryInsights
				? {
						companyKeyFigures: React.createRef()
						// We are not creating a ref for company comparison here because we will create it in the componentDidUpdate method
						// which occurs when the industry data is fetched
				  }
				: {}),
			incomeStatement: React.createRef(),
			balanceSheet: React.createRef(),
			keyFigures: React.createRef()
		};

		this.turnover = [];
		this.result = [];
		this.years = [];
		this.columns = [{ title: '' }];
		if (this.props.companyData && this.props.companyData.length !== 0) {
			// Always sort to get newest first, then take the 5 most recent
			this.sortedAccounts = this.props.companyData.sort((a, b) => {
				return moment(a.dateTo) < moment(b.dateTo) ? 1 : -1;
			});
			if (this.sortedAccounts.length > 5) {
				this.sortedAccounts = this.sortedAccounts.slice(0, 5);
			}

			const shouldReverse = this.state.chartOrder === 'asc';
			if (shouldReverse) {
				this.sortedAccounts.sort((a, b) => {
					return moment(a.dateTo) < moment(b.dateTo) ? -1 : 1;
				});
			}
			for (const account of this.sortedAccounts) {
				this.turnover.push(account.plNetOperatingIncome * 1000);
				this.result.push(account.plOperatingResult * 1000);
				const currentYear = moment(account.dateTo).format('YYYY');
				this.years.push(currentYear);
				this.columns.push({ title: currentYear });
			}

			while (this.years.length < 5) {
				if (shouldReverse) {
					const oldestYear = moment(this.years[0] + '-01-01')
						.subtract(1, 'year')
						.format('YYYY');
					this.years.unshift(oldestYear);
					this.turnover.unshift(0);
					this.result.unshift(0);
					this.columns.unshift({ title: oldestYear });
				} else {
					const oldestYear = moment(this.years[this.years.length - 1] + '-01-01')
						.subtract(1, 'year')
						.format('YYYY');
					this.years.push(oldestYear);
					this.turnover.push(0);
					this.result.push(0);
					this.columns.push({ title: oldestYear });
				}
			}

			if (shouldReverse) {
				this.years.reverse();
				this.turnover.reverse();
				this.result.reverse();
				this.columns.reverse();
			}
		}
		this.scrollDiff = this.hasReactClientCard ? 120 : 70;
		this.stickyPos = this.showPlaceholder ? 440 : 185;

		this.lang = {
			revenue: t('financials.revenue'),
			keyFigures: t('financials.keyFigures'),
			incomeStatement: t('financials.incomeStatement'),
			balanceSheet: t('financials.balanceSheet'),
			annualResult: t('financials.annualResult', { year: this.years[0] }),
			companyKeyFigures: t('financials.companyKeyFigures'),
			companyComparison: t('financials.companyComparison')
		};
		this.getSummaryPromise = React.createRef();

		this.listener = Tools.$rootScope.$on('economyChartOrder.changed', (e, newChartOrder) => {
			if (newChartOrder && newChartOrder !== this.state.chartOrder) {
				this.setState({ chartOrder: newChartOrder });
			}
		});
	}

	fetchIndustryData = () => {
		if (this.props.sniCode) {
			this.setState({
				industryData: {
					code: this.props.sniCode,
					description: translateSniCode(this.props.sniCode) || ''
				}
			});
			return;
		} else {
			this.setState({ industryData: null, industryLoading: true });
			if (!this.props.prospectingId) {
				this.setState({ industryLoading: false });
				return;
			}

			this.getSummaryPromise.current = makeCancelable(ClientIndustryResource.get(this.props.prospectingId));

			this.getSummaryPromise.current.promise
				.then(response => {
					if (response.data) {
						this.setState({
							industryData: {
								code: response.data.cpvCode,
								description: response.data.description
							}
						});
					}
				})
				.catch(err => logError(err, 'Failed to fetch account summary'))
				.finally(() => this.setState({ industryLoading: false }));
		}
	};

	componentDidMount() {
		window.addEventListener('scroll', this.handleScroll.bind(this), true);
		if (this.hasIndustryInsights) {
			this.fetchIndustryData();
		}
	}

	componentDidUpdate() {
		if (this.hasIndustryInsights && this.state.industryData && !this.tabRefs.companyComparison) {
			this.tabRefs.companyComparison = React.createRef();
			this.setState({ hasCompanyComparisonRef: true });
		}
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScroll.bind(this), true);
		if (this.getPromise) {
			this.getPromise.cancel();
		}
		if (this.getSummaryPromise.current) {
			this.getSummaryPromise.current.cancel();
		}

		this.listener();
	}

	updateActiveTab(scrollTop) {
		const tabs = ['incomeStatement', 'balanceSheet', 'keyFigures'];

		if (this.hasIndustryInsights) {
			if (this.tabRefs?.companyComparison) {
				tabs.unshift('companyComparison');
			}
			tabs.unshift('companyKeyFigures');
		}

		for (let i = 0; i < tabs.length; i++) {
			const currentTab = tabs[i];
			const nextTab = tabs[i + 1];

			const currentOffset = this.tabRefs[currentTab]?.current?.offsetTop ?? Infinity;
			const nextOffset = nextTab ? this.tabRefs[nextTab]?.current?.offsetTop ?? Infinity : Infinity;

			if (i === 0) {
				if (this.state.tab !== currentTab && scrollTop < nextOffset - this.scrollDiff) {
					this.setState({ tab: currentTab });
					break;
				}
			} else {
				if (
					this.state.tab !== currentTab &&
					scrollTop >= currentOffset - this.scrollDiff &&
					scrollTop < nextOffset - this.scrollDiff
				) {
					this.setState({ tab: currentTab });
					break;
				}
			}
		}
	}

	handleScroll() {
		const scrollTop = window.pageYOffset;
		const requiredRefs = [this.tabRefs.incomeStatement, this.tabRefs.balanceSheet, this.tabRefs.keyFigures];

		if (this.hasIndustryInsights) {
			// We don't need to check for industryData because the componentDidUpdate method creates this ref
			// when the industry data is fetched
			if (this.tabRefs?.companyComparison) {
				requiredRefs.push(this.tabRefs.companyComparison);
			}
			requiredRefs.push(this.tabRefs.companyKeyFigures);
		}

		if (requiredRefs.some(ref => !ref.current)) {
			return;
		}

		this.updateActiveTab(scrollTop);

		if (!(this.state.sticky || this.hasIndustryInsights) && scrollTop > this.stickyPos) {
			const element = document.getElementById('Tabs');
			if (!element) {
				return;
			}
			const width = element.getBoundingClientRect().width;
			this.setState({ sticky: true, width });
		} else if ((this.state.sticky || this.hasIndustryInsights) && scrollTop <= this.stickyPos) {
			this.setState({ sticky: false });
		}
	}

	onChangeTab(tab) {
		switch (tab) {
			case 'companyKeyFigures':
				window.scrollTo({
					top: this.tabRefs.companyKeyFigures.current.offsetTop - this.scrollDiff,
					behavior: 'smooth'
				});
				break;
			case 'companyComparison':
				window.scrollTo({
					top: this.tabRefs.companyComparison.current.offsetTop - this.scrollDiff,
					behavior: 'smooth'
				});
				break;
			case 'balanceSheet':
				window.scrollTo({
					top: this.tabRefs.balanceSheet.current.offsetTop - this.scrollDiff,
					behavior: 'smooth',
					block: 'start'
				});
				break;
			case 'incomeStatement':
				window.scrollTo({
					top: this.tabRefs.incomeStatement.current.offsetTop - this.scrollDiff,
					behavior: 'smooth',
					block: 'start'
				});
				break;
			case 'keyFigures':
				window.scrollTo({
					top: this.tabRefs.keyFigures.current.offsetTop - this.scrollDiff,
					behavior: 'smooth',
					block: 'start'
				});
				break;
		}
	}

	formattedValue(value) {
		const formatted = Tools.$filter('currencyFormat')(value, this.currency);
		return [formatted.replace(this.currency, ''), this.currency];
	}

	saveChartOrder = async newChartOrder => {
		try {
			await Tools.UserParam.save(this.ECONOMY_CHART_ORDER_PARAM_ID, newChartOrder, {
				skipNotification: true
			});

			Tools.AppService.setSelf({
				...this.self,
				userParams: {
					...this.self.userParams,
					economyChartOrder: newChartOrder
				}
			});

			Tools.$rootScope.$broadcast('economyChartOrder.changed', newChartOrder);
		} catch (e) {
			logError(e, 'Error saving economy chart order');
		}
	};

	handleChartOrderChange = newChartOrder => {
		this.saveChartOrder(newChartOrder);
		this.setState({ chartOrder: newChartOrder });
	};

	getOrderedYears = () => {
		const mutatedYears = [...this.years];

		if (this.state.chartOrder === 'asc') {
			return mutatedYears.reverse();
		}

		return mutatedYears;
	};

	render() {
		if (
			(!this.props.companyData && !this.showPlaceholder) ||
			(!this.showPlaceholder && this.props.companyData && this.props.companyData.length === 0) ||
			!this.props.headquarters
		) {
			return null;
		}

		const classNames = new bemClass('FinancialTab');

		const options = [
			{ value: 'desc', icon: 'arrow-left', tooltip: T('financials.chartOrder.left') },
			{ value: 'asc', icon: 'arrow-right', tooltip: T('financials.chartOrder.right') }
		];

		const incomeStatementKey = `incomeStatement-${this.state.chartOrder}`;
		const balanceSheetKey = `balanceSheet-${this.state.chartOrder}`;
		const keyFiguresKey = `keyFigures-${this.state.chartOrder}`;

		let formattedTurnoverValue = null,
			formattedTurnoverUnit = null,
			formattedResultValue = null,
			formattedResultUnit = null;
		if (!this.showPlaceholder) {
			[formattedTurnoverValue, formattedTurnoverUnit] = this.formattedValue(this.turnover[0]);
			[formattedResultValue, formattedResultUnit] = this.formattedValue(this.result[0]);
		}

		const tabs = [
			...(this.hasIndustryInsights
				? [
						{
							key: 'company-key-figures',
							id: 'companyKeyFigures',
							label: this.lang.companyKeyFigures
						},
						...(this.tabRefs?.companyComparison
							? [
									{
										key: 'company-comparison',
										id: 'companyComparison',
										label: this.lang.companyComparison
									}
							  ]
							: [])
				  ]
				: []),
			{
				key: 'income-statement',
				id: 'incomeStatement',
				label: this.lang.incomeStatement
			},
			{
				key: 'balance-sheet',
				id: 'balanceSheet',
				label: this.lang.balanceSheet
			},
			{
				key: 'key-figures',
				id: 'keyFigures',
				label: this.lang.keyFigures
			}
		];

		return (
			<div className={classNames.b()}>
				{this.showPlaceholder ? (
					<div className={classNames.elem('addon-banner').b()}>
						<BillingAddonBanner
							alias={this.alias}
							chipText={T('admin.billing.addon.PROSPECTING_PRO.name')}
							text={T('prospecting.financialDataDescription')}
							title={T('prospecting.showFinancialDataFor', { companyName: this.props.accountName })}
							location="clientFinancials"
							nextSteps={[
								{
									title: T('prospecting.showFinancialDataFor', {
										companyName: this.props.accountName
									}),
									// reloading the page is the easiest way to update the page right now since everything is fetched from the angular controller
									action: () =>
										Tools.$state.go(Tools.$state.current.name, Tools.$state.params, {
											reload: true
										})
								},
								...(NEXT_STEPS[this.alias] || [])
							]}
						></BillingAddonBanner>
					</div>
				) : null}
				{!this.showPlaceholder ? (
					<React.Fragment>
						<Card color={this.showPlaceholder ? 'grey-1' : 'white'}>
							{!this.hasIndustryInsights ? (
								<Row className={classNames.elem('finacialInformation').b()}>
									<Column className={classNames.elem('keyFigures').b()}>
										<div className={classNames.elem('revenue').b()}>
											<Text color="grey-10">
												<Icon
													name="square"
													color={this.showPlaceholder ? 'grey-5' : 'bright-blue'}
												/>
												{this.lang.revenue}
											</Text>
											<div>
												{this.showPlaceholder ? (
													<Text
														className={classNames.elem('greyLineNumber').b()}
														color="grey-5"
														size="xl"
														bold={true}
													>
														{'------'}
													</Text>
												) : (
													<Title className={classNames.elem('bigNumbers').b()} size="lg">
														{formattedTurnoverValue}
													</Title>
												)}
												<Text
													className={classNames.elem('smallUnits').b()}
													size="sm"
													color="grey-10"
												>
													{this.showPlaceholder ? null : formattedTurnoverUnit}
												</Text>
											</div>
										</div>
										<div>
											<Text color="grey-10">
												<Icon
													name="square"
													color={
														this.showPlaceholder
															? 'grey-5'
															: formattedResultValue.indexOf('-') !== -1
															? 'red'
															: 'bright-green'
													}
												/>
												{this.lang.annualResult}
											</Text>
											<div>
												{this.showPlaceholder ? (
													<Text
														className={classNames.elem('greyLineNumber').b()}
														color={'grey-5'}
														size="xl"
														bold={true}
													>
														{'------'}
													</Text>
												) : (
													<Title className={classNames.elem('bigNumbers').b()} size="lg">
														{formattedResultValue}
													</Title>
												)}
												<Text
													className={classNames.elem('smallUnits').b()}
													size="sm"
													color="grey-10"
												>
													{this.showPlaceholder ? null : formattedResultUnit}
												</Text>
											</div>
										</div>
									</Column>
									<Column>
										<ButtonSelect
											className={classNames.elem('chartOrder').b()}
											value={this.state.chartOrder}
											onChange={this.handleChartOrderChange}
											options={options}
										></ButtonSelect>
										<FinancialChart
											placeholder={this.showPlaceholder}
											turnover={this.turnover}
											result={this.result}
											years={this.years}
											currency={this.currency}
											chartOrder={this.state.chartOrder}
										/>
									</Column>
								</Row>
							) : null}
							<Tabs
								id="Tabs"
								style={
									this.state.sticky || this.hasIndustryInsights ? { width: this.state.width } : null
								}
								className={
									this.state.sticky || this.hasIndustryInsights
										? classNames.elem('tabs').elem('sticky').b()
										: classNames.elem('tabs').elem('normal').b()
								}
								selected={this.state.tab}
								onChange={tab => this.onChangeTab(tab)}
							>
								{tabs.map(tab => (
									<Tab
										key={tab.key}
										id={tab.id}
										className={classNames.elem('tabs').mod({ grey: this.showPlaceholder }).b()}
									>
										{tab.label}
									</Tab>
								))}
							</Tabs>
							{this.state.sticky || this.hasIndustryInsights ? (
								<React.Fragment>
									<div
										className={classNames
											.elem('tabs')
											.elem('filler')
											.mod({ grey: this.showPlaceholder })
											.b()}
									></div>
									<div className={classNames.elem('tabs').elem('placeholder').b()}></div>
								</React.Fragment>
							) : null}
						</Card>
						{this.hasIndustryInsights ? (
							<IndustryInsightsCard
								industryData={this.state.industryData}
								accountId={this.props.accountId}
								accountName={this.props.accountName}
								references={{
									companyKeyFigures: this.tabRefs.companyKeyFigures,
									...(this.tabRefs?.companyComparison
										? { companyComparison: this.tabRefs.companyComparison }
										: {})
								}}
								showPlaceholder={this.state.industryLoading}
								financialInfo={{
									turnover: this.turnover,
									result: this.result,
									years: this.years,
									currency: this.currency,
									formattedTurnover: [formattedTurnoverValue, formattedTurnoverUnit],
									formattedResult: [formattedResultValue, formattedResultUnit]
								}}
							/>
						) : null}

						<FinancialCard
							key={incomeStatementKey}
							type="incomeStatement"
							accounts={this.sortedAccounts}
							years={this.getOrderedYears()}
							reference={this.tabRefs.incomeStatement}
							showPlaceholder={this.showPlaceholder}
							currency={this.currency}
							hasIndustryInsights={this.hasIndustryInsights}
						/>

						<FinancialCard
							key={balanceSheetKey}
							type="balanceSheet"
							accounts={this.sortedAccounts}
							years={this.getOrderedYears()}
							reference={this.tabRefs.balanceSheet}
							showPlaceholder={this.showPlaceholder}
							currency={this.currency}
							hasIndustryInsights={this.hasIndustryInsights}
						/>

						<FinancialCard
							key={keyFiguresKey}
							type="keyFigures"
							accounts={this.sortedAccounts}
							years={this.getOrderedYears()}
							reference={this.tabRefs.keyFigures}
							showPlaceholder={this.showPlaceholder}
							currency={this.currency}
							hasIndustryInsights={this.hasIndustryInsights}
						/>
					</React.Fragment>
				) : null}
			</div>
		);
	}
}
FinancialTab.propTypes = {
	accountId: PropTypes.number,
	accountName: PropTypes.string,
	companyData: PropTypes.array,
	revenue: PropTypes.number,
	headquarters: PropTypes.number,
	sniCode: PropTypes.string,
	prospectingId: PropTypes.string
};
export default FinancialTab;
